import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Terminal, RefreshCw } from "lucide-react";
import axios from "axios";
import Layout from "../components/custom/Layout";
import { API } from "../contexts/AuthContext";

const LEVEL_COLORS = {
  info: "#38BDF8",
  warning: "#F59E0B",
  error: "#EF4444",
  success: "#22C55E",
};

const LEVEL_PREFIX = {
  info: "[INFO]",
  warning: "[WARN]",
  error: "[ERROR]",
  success: "[OK]",
};

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const bottomRef = useRef(null);

  const fetchLogs = async () => {
    try {
      const [lRes, sRes] = await Promise.all([
        axios.get(`${API}/logs?limit=200`),
        axios.get(`${API}/dashboard/summary`),
      ]);
      setLogs(lRes.data);
      setSummary(sRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, []);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  const filtered = filter === "all" ? logs : logs.filter(l => l.level === filter);

  return (
    <Layout summary={summary}>
      <div className="p-7" data-testid="logs-page">
        <div className="flex items-center justify-between mb-7">
          <div>
            <h1 className="flex items-center gap-2" style={{
              fontFamily: "'Inter Tight', sans-serif",
              fontWeight: "700",
              color: "#E2E8F0",
              fontSize: "16px",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}>
              <Terminal size={16} className="text-[#38BDF8]" /> System Logs
            </h1>
            <p style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "10px", color: "#475569", marginTop: "3px", fontWeight: "500", letterSpacing: "0.08em" }}>
              {filtered.length} log entries
            </p>
          </div>
          <div className="flex items-center gap-2">
            {["all", "info", "warning", "error"].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`log-filter-${f}`}
                className="px-3 py-1.5 octa-clip-sm transition-all"
                style={{
                  fontFamily: "'Inter Tight', sans-serif",
                  fontWeight: "600",
                  fontSize: "9px",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  background: filter === f ? "rgba(37,99,235,0.1)" : "transparent",
                  color: filter === f ? "#2563EB" : "#475569",
                  border: `1px solid ${filter === f ? "rgba(37,99,235,0.25)" : "rgba(255,255,255,0.06)"}`,
                }}
              >
                {f}
              </button>
            ))}
            <button
              onClick={fetchLogs}
              data-testid="refresh-logs-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 octa-clip-sm border transition-all"
              style={{ borderColor: "rgba(255,255,255,0.06)", color: "#475569", fontSize: "10px" }}
            >
              <RefreshCw size={11} />
            </button>
          </div>
        </div>

        {/* Terminal */}
        <div className="octa-clip min-h-96 max-h-[calc(100vh-200px)] overflow-auto p-5" style={{
          background: "#0A0B0E",
          border: "1px solid rgba(255,255,255,0.03)",
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "11px",
        }} data-testid="logs-terminal">
          {loading && (
            <div style={{ color: "#38BDF8" }} className="animate-pulse">Initializing log stream...</div>
          )}
          {!loading && filtered.length === 0 && (
            <div style={{ color: "#475569" }}>No log entries found.</div>
          )}
          {!loading && filtered.map((log, i) => {
            const color = LEVEL_COLORS[log.level] || "#94A3B8";
            const prefix = LEVEL_PREFIX[log.level] || "[LOG]";
            const ts = log.created_at ? new Date(log.created_at).toISOString().replace("T", " ").slice(0, 19) : "--";
            return (
              <motion.div
                key={log._id}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.12, delay: Math.min(i * 0.015, 0.5) }}
                className="flex gap-3 py-0.5 hover:bg-white/[0.015] px-1"
                data-testid={`log-entry-${log._id}`}
              >
                <span style={{ color: "#334155", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{ts}</span>
                <span style={{ color, flexShrink: 0, fontWeight: "600" }}>{prefix}</span>
                <span style={{ color: "#F59E0B", flexShrink: 0 }}>{log.action}</span>
                <span style={{ color: "#94A3B8" }}>{log.details}</span>
              </motion.div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Legend */}
        <div className="flex items-center gap-5 mt-4">
          {Object.entries(LEVEL_COLORS).map(([level, color]) => (
            <div key={level} className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
              <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "9px", color: "#475569", fontWeight: "500", letterSpacing: "0.06em", textTransform: "capitalize" }}>{level}</span>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
